'use client';

import { useState, useMemo, useEffect } from 'react';
import Link from 'next/link';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';
import { saveToolScore } from '@/lib/tool-score-storage';

interface ChecklistItem {
  id: string;
  category: string;
  item: string;
  description: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  frameworks: string[]; // OWASP, SOC 2, etc.
}

interface ChecklistState {
  [key: string]: 'not-started' | 'in-progress' | 'completed' | 'not-applicable';
}

export default function SecurityAssessmentChecklistPage() {
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'security-assessment-checklist',
    toolCategory: 'maturity',
  });

  const checklistItems: ChecklistItem[] = [
    // Authentication & Access Control
    {
      id: 'auth-1',
      category: 'Authentication & Access Control',
      item: 'Multi-factor authentication (MFA) enforced',
      description: 'Require MFA for all user accounts, especially administrative access',
      priority: 'critical',
      frameworks: ['OWASP', 'SOC 2'],
    },
    {
      id: 'auth-2',
      category: 'Authentication & Access Control',
      item: 'Strong password policy implemented',
      description: 'Minimum length, complexity requirements, password rotation',
      priority: 'high',
      frameworks: ['OWASP', 'SOC 2'],
    },
    {
      id: 'auth-3',
      category: 'Authentication & Access Control',
      item: 'Role-based access control (RBAC) configured',
      description: 'Users have minimum necessary permissions based on roles',
      priority: 'high',
      frameworks: ['SOC 2'],
    },
    {
      id: 'auth-4',
      category: 'Authentication & Access Control',
      item: 'Session management secure',
      description: 'Proper session timeouts, secure cookies, token rotation',
      priority: 'critical',
      frameworks: ['OWASP'],
    },
    {
      id: 'auth-5',
      category: 'Authentication & Access Control',
      item: 'Account lockout after failed attempts',
      description: 'Prevent brute force attacks with account lockout policies',
      priority: 'medium',
      frameworks: ['OWASP'],
    },

    // Data Protection
    {
      id: 'data-1',
      category: 'Data Protection',
      item: 'Data encrypted at rest',
      description: 'All sensitive data encrypted using strong encryption (AES-256)',
      priority: 'critical',
      frameworks: ['SOC 2', 'GDPR'],
    },
    {
      id: 'data-2',
      category: 'Data Protection',
      item: 'Data encrypted in transit',
      description: 'TLS 1.2+ for all network communications',
      priority: 'critical',
      frameworks: ['OWASP', 'SOC 2'],
    },
    {
      id: 'data-3',
      category: 'Data Protection',
      item: 'Sensitive data minimization',
      description: 'Only collect and store necessary sensitive data',
      priority: 'high',
      frameworks: ['GDPR', 'SOC 2'],
    },
    {
      id: 'data-4',
      category: 'Data Protection',
      item: 'PII/PHI handling procedures',
      description: 'Proper procedures for handling personally identifiable information',
      priority: 'critical',
      frameworks: ['GDPR', 'HIPAA'],
    },
    {
      id: 'data-5',
      category: 'Data Protection',
      item: 'Data backup and recovery tested',
      description: 'Regular backups with tested recovery procedures',
      priority: 'high',
      frameworks: ['SOC 2'],
    },
    {
      id: 'data-6',
      category: 'Data Protection',
      item: 'Secure data deletion procedures',
      description: 'Proper data sanitization when deleting sensitive information',
      priority: 'medium',
      frameworks: ['GDPR', 'SOC 2'],
    },

    // Application Security
    {
      id: 'app-1',
      category: 'Application Security',
      item: 'Input validation implemented',
      description: 'All user inputs validated and sanitized',
      priority: 'critical',
      frameworks: ['OWASP Top 10'],
    },
    {
      id: 'app-2',
      category: 'Application Security',
      item: 'SQL injection prevention',
      description: 'Parameterized queries, ORM usage, input sanitization',
      priority: 'critical',
      frameworks: ['OWASP Top 10'],
    },
    {
      id: 'app-3',
      category: 'Application Security',
      item: 'XSS (Cross-Site Scripting) prevention',
      description: 'Output encoding, CSP headers, sanitization',
      priority: 'critical',
      frameworks: ['OWASP Top 10'],
    },
    {
      id: 'app-4',
      category: 'Application Security',
      item: 'CSRF protection implemented',
      description: 'Anti-CSRF tokens for state-changing operations',
      priority: 'high',
      frameworks: ['OWASP Top 10'],
    },
    {
      id: 'app-5',
      category: 'Application Security',
      item: 'Security headers configured',
      description: 'CSP, X-Frame-Options, HSTS, X-Content-Type-Options',
      priority: 'high',
      frameworks: ['OWASP'],
    },
    {
      id: 'app-6',
      category: 'Application Security',
      item: 'API rate limiting implemented',
      description: 'Prevent abuse and DoS attacks on APIs',
      priority: 'high',
      frameworks: ['OWASP'],
    },
    {
      id: 'app-7',
      category: 'Application Security',
      item: 'Dependency vulnerability scanning',
      description: 'Regular scanning of third-party dependencies for vulnerabilities',
      priority: 'high',
      frameworks: ['OWASP'],
    },

    // Infrastructure Security
    {
      id: 'infra-1',
      category: 'Infrastructure Security',
      item: 'Firewall configured and maintained',
      description: 'Network segmentation and firewall rules properly configured',
      priority: 'critical',
      frameworks: ['SOC 2'],
    },
    {
      id: 'infra-2',
      category: 'Infrastructure Security',
      item: 'Regular security patching',
      description: 'OS and software patches applied within SLA timeframes',
      priority: 'critical',
      frameworks: ['SOC 2'],
    },
    {
      id: 'infra-3',
      category: 'Infrastructure Security',
      item: 'Intrusion detection/prevention system',
      description: 'IDS/IPS monitoring for suspicious activity',
      priority: 'high',
      frameworks: ['SOC 2'],
    },
    {
      id: 'infra-4',
      category: 'Infrastructure Security',
      item: 'DDoS protection configured',
      description: 'Protection against distributed denial of service attacks',
      priority: 'high',
      frameworks: [],
    },
    {
      id: 'infra-5',
      category: 'Infrastructure Security',
      item: 'Secure container configuration',
      description: 'Container images scanned, minimal base images, no root access',
      priority: 'high',
      frameworks: [],
    },
    {
      id: 'infra-6',
      category: 'Infrastructure Security',
      item: 'Secrets management system',
      description: 'Proper storage and rotation of API keys, passwords, certificates',
      priority: 'critical',
      frameworks: ['SOC 2'],
    },

    // Monitoring & Logging
    {
      id: 'monitor-1',
      category: 'Monitoring & Logging',
      item: 'Security event logging enabled',
      description: 'Log authentication, authorization, and security events',
      priority: 'critical',
      frameworks: ['SOC 2'],
    },
    {
      id: 'monitor-2',
      category: 'Monitoring & Logging',
      item: 'Log retention policy defined',
      description: 'Appropriate retention periods for different log types',
      priority: 'medium',
      frameworks: ['SOC 2'],
    },
    {
      id: 'monitor-3',
      category: 'Monitoring & Logging',
      item: 'Security monitoring and alerting',
      description: 'Real-time alerts for suspicious activities',
      priority: 'high',
      frameworks: ['SOC 2'],
    },
    {
      id: 'monitor-4',
      category: 'Monitoring & Logging',
      item: 'Log integrity protection',
      description: 'Logs protected from tampering and unauthorized access',
      priority: 'high',
      frameworks: ['SOC 2'],
    },

    // Compliance & Governance
    {
      id: 'comp-1',
      category: 'Compliance & Governance',
      item: 'Security policy documented',
      description: 'Written information security policy',
      priority: 'high',
      frameworks: ['SOC 2'],
    },
    {
      id: 'comp-2',
      category: 'Compliance & Governance',
      item: 'Incident response plan',
      description: 'Documented procedures for security incident response',
      priority: 'critical',
      frameworks: ['SOC 2'],
    },
    {
      id: 'comp-3',
      category: 'Compliance & Governance',
      item: 'Security awareness training',
      description: 'Regular security training for all employees',
      priority: 'high',
      frameworks: ['SOC 2'],
    },
    {
      id: 'comp-4',
      category: 'Compliance & Governance',
      item: 'Vendor security assessments',
      description: 'Third-party vendor security reviews',
      priority: 'medium',
      frameworks: ['SOC 2'],
    },
    {
      id: 'comp-5',
      category: 'Compliance & Governance',
      item: 'Regular security audits',
      description: 'Periodic internal and external security assessments',
      priority: 'high',
      frameworks: ['SOC 2'],
    },
    {
      id: 'comp-6',
      category: 'Compliance & Governance',
      item: 'Penetration testing conducted',
      description: 'Regular penetration testing by qualified professionals',
      priority: 'high',
      frameworks: ['SOC 2'],
    },
  ];

  const [checklist, setChecklist] = useState<ChecklistState>(() => {
    const initial: ChecklistState = {};
    checklistItems.forEach(item => {
      initial[item.id] = 'not-started';
    });
    return initial;
  });

  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterFramework, setFilterFramework] = useState<string>('all');

  const updateChecklistItem = (id: string, status: ChecklistState[string]) => {
    setChecklist(prev => ({ ...prev, [id]: status }));
  };

  const categories = useMemo(() => {
    return Array.from(new Set(checklistItems.map(item => item.category)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const frameworks = useMemo(() => {
    const allFrameworks = new Set<string>();
    checklistItems.forEach(item => {
      item.frameworks.forEach(fw => allFrameworks.add(fw));
    });
    return Array.from(allFrameworks);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredItems = useMemo(() => {
    return checklistItems.filter(item => {
      if (filterPriority !== 'all' && item.priority !== filterPriority) return false;
      if (filterCategory !== 'all' && item.category !== filterCategory) return false;
      if (filterFramework !== 'all' && !item.frameworks.includes(filterFramework)) return false;
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterPriority, filterCategory, filterFramework]);

  const stats = useMemo(() => {
    const total = checklistItems.length;
    const completed = Object.values(checklist).filter(s => s === 'completed').length;
    const inProgress = Object.values(checklist).filter(s => s === 'in-progress').length;
    const notApplicable = Object.values(checklist).filter(s => s === 'not-applicable').length;
    const notStarted = Object.values(checklist).filter(s => s === 'not-started').length;

    const applicable = total - notApplicable;
    const completionRate = applicable > 0 ? (completed / applicable) * 100 : 0;

    return {
      total,
      completed,
      inProgress,
      notApplicable,
      notStarted,
      applicable,
      completionRate,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checklist]);

  useEffect(() => {
    saveToolScore('security-assessment-checklist', stats.completionRate);
  }, [stats.completionRate]);

  const priorityColors = {
    'critical': 'text-red-500',
    'high': 'text-accent',
    'medium': 'text-yellow-500',
    'low': 'text-foreground-tertiary',
  };

  const priorityLabels = {
    'critical': '🔴 Critical',
    'high': '🟠 High',
    'medium': '🟡 Medium',
    'low': '⚪ Low',
  };

  const statusColors = {
    'not-started': 'bg-background border-border',
    'in-progress': 'bg-yellow-500/10 border-yellow-500',
    'completed': 'bg-green-500/10 border-green-500',
    'not-applicable': 'bg-cream/5 border-border-muted',
  };

  const statusLabels = {
    'not-started': 'Not Started',
    'in-progress': 'In Progress',
    'completed': 'Completed',
    'not-applicable': 'Not Applicable',
  };

  const groupedItems = useMemo(() => {
    const grouped: { [key: string]: ChecklistItem[] } = {};
    filteredItems.forEach(item => {
      if (!grouped[item.category]) {
        grouped[item.category] = [];
      }
      grouped[item.category].push(item);
    });
    return grouped;
  }, [filteredItems]);

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!commandCenterEnabled) return;

    // Get gaps - items not completed that are critical or high priority
    const gaps = checklistItems
      .filter(item => checklist[item.id] !== 'completed' && checklist[item.id] !== 'not-applicable')
      .filter(item => item.priority === 'critical' || item.priority === 'high')
      .map(item => ({
        id: item.id,
        title: item.item,
        severity: item.priority === 'critical' ? 'critical' as const : 'high' as const,
        description: `${item.category}: ${item.description}`,
      }));

    // Get recommendations based on incomplete items by category
    const categoryGaps = categories.map(cat => {
      const catItems = checklistItems.filter(item => item.category === cat);
      const incomplete = catItems.filter(item =>
        checklist[item.id] !== 'completed' && checklist[item.id] !== 'not-applicable'
      );
      const completed = catItems.filter(item => checklist[item.id] === 'completed');
      return {
        category: cat,
        incompleteCritical: incomplete.filter(i => i.priority === 'critical').length,
        incompleteHigh: incomplete.filter(i => i.priority === 'high').length,
        completionRate: catItems.length > 0 ? (completed.length / catItems.length) * 100 : 0,
      };
    }).filter(c => c.incompleteCritical > 0 || c.incompleteHigh > 0);

    const recommendations = categoryGaps
      .sort((a, b) => {
        // Sort by critical first, then high
        if (a.incompleteCritical !== b.incompleteCritical) {
          return b.incompleteCritical - a.incompleteCritical;
        }
        return b.incompleteHigh - a.incompleteHigh;
      })
      .slice(0, 6)
      .map(cat => ({
        id: cat.category.toLowerCase().replace(/[^a-z0-9]/g, '-'),
        title: `Address ${cat.category} gaps`,
        priority: cat.incompleteCritical > 0 ? 'high' as const : 'medium' as const,
        description: `${cat.incompleteCritical} critical and ${cat.incompleteHigh} high priority items remaining. Current completion: ${cat.completionRate.toFixed(0)}%`,
      }));

    // Calculate scores by category
    const scores: Record<string, number> = {};
    categories.forEach(cat => {
      const catItems = checklistItems.filter(item => item.category === cat);
      const applicableItems = catItems.filter(item => checklist[item.id] !== 'not-applicable');
      const completed = applicableItems.filter(item => checklist[item.id] === 'completed');
      scores[cat.toLowerCase().replace(/[^a-z0-9]/g, '-')] = applicableItems.length > 0
        ? (completed.length / applicableItems.length) * 100
        : 0;
    });

    const assessment = await saveResult({
      name: `Security Assessment - ${new Date().toLocaleDateString()}`,
      score_overall: stats.completionRate,
      scores,
      answers: checklist,
      gaps,
      recommendations,
    });

    if (assessment) {
      setSaved(true);
    }
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-4xl">🔐</span>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground">
              Security Assessment Checklist
            </h1>
          </div>
          <p className="text-xl text-foreground-secondary">
            Interactive checklist for security reviews and compliance assessments
          </p>
        </div>

        {/* Breadcrumb */}
        <nav className="mb-8 text-sm text-foreground-tertiary">
          <Link href="/" className="hover:text-accent transition-colors">
            Home
          </Link>
          <span className="mx-2">/</span>
          <Link href="/tools" className="hover:text-accent transition-colors">
            Tools
          </Link>
          <span className="mx-2">/</span>
          <span className="text-foreground">Security Assessment Checklist</span>
        </nav>

        {/* Instructions */}
        <div className="bg-accent-muted border border-border rounded-lg p-6 mb-8">
          <h3 className="font-bold text-accent mb-2">How to use this checklist</h3>
          <ol className="text-foreground-secondary text-sm space-y-2 list-decimal list-inside">
            <li>Review each security control and mark its status</li>
            <li>Use filters to focus on specific priorities or frameworks</li>
            <li>Track your completion rate and identify gaps</li>
            <li>Export or document findings for compliance reporting</li>
          </ol>
        </div>

        {/* Stats Dashboard */}
        <div className="bg-surface rounded-lg border border-border p-6 mb-8">
          <h3 className="text-xl font-bold text-foreground mb-4">Assessment Progress</h3>

          <div className="grid md:grid-cols-5 gap-4 mb-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-green-500">{stats.completed}</div>
              <div className="text-sm text-foreground-tertiary">Completed</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-500">{stats.inProgress}</div>
              <div className="text-sm text-foreground-tertiary">In Progress</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-foreground-tertiary">{stats.notStarted}</div>
              <div className="text-sm text-foreground-tertiary">Not Started</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-foreground-muted">{stats.notApplicable}</div>
              <div className="text-sm text-foreground-tertiary">Not Applicable</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-accent">{stats.completionRate.toFixed(0)}%</div>
              <div className="text-sm text-foreground-tertiary">Complete</div>
            </div>
          </div>

          <div className="mb-2">
            <div className="h-4 bg-background rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-accent to-green-500 transition-all"
                style={{ width: `${stats.completionRate}%` }}
              />
            </div>
          </div>
          <div className="text-xs text-foreground-tertiary mb-4">
            {stats.completed} of {stats.applicable} applicable items completed
          </div>

          {/* Save to Command Center */}
          {commandCenterEnabled && !saved && (
            <div className="border-t border-border-muted pt-4">
              <button
                onClick={handleSaveToCommandCenter}
                disabled={saving}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500 transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save to Command Center'}
              </button>
            </div>
          )}
          {saved && (
            <div className="border-t border-border-muted pt-4">
              <div className="w-full px-4 py-3 bg-green-600/20 text-green-400 rounded-lg font-medium flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Saved to Command Center
              </div>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="bg-surface rounded-lg border border-border p-6 mb-8">
          <h3 className="text-lg font-bold text-foreground mb-4">Filters</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">Priority</label>
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
              >
                <option value="all">All Priorities</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">Category</label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">Framework</label>
              <select
                value={filterFramework}
                onChange={(e) => setFilterFramework(e.target.value)}
                className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
              >
                <option value="all">All Frameworks</option>
                {frameworks.map(fw => (
                  <option key={fw} value={fw}>{fw}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Checklist Items by Category */}
        <div className="space-y-8">
          {Object.entries(groupedItems).map(([category, items]) => (
            <div key={category} className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-2xl font-bold text-foreground mb-4">{category}</h3>

              <div className="space-y-4">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className={`border rounded-lg p-4 transition-all ${statusColors[checklist[item.id]]}`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Checkbox */}
                      <div className="flex-shrink-0 mt-1">
                        <input
                          type="checkbox"
                          checked={checklist[item.id] === 'completed'}
                          onChange={(e) => updateChecklistItem(item.id, e.target.checked ? 'completed' : 'not-started')}
                          className="w-5 h-5 cursor-pointer"
                        />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-bold text-foreground">{item.item}</h4>
                              <span className={`text-xs ${priorityColors[item.priority]}`}>
                                {priorityLabels[item.priority].split(' ')[0]}
                              </span>
                            </div>
                            <p className="text-sm text-foreground-secondary">{item.description}</p>
                          </div>
                        </div>

                        {/* Frameworks and Status */}
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex flex-wrap gap-2">
                            {item.frameworks.map(fw => (
                              <span key={fw} className="text-xs px-2 py-1 bg-accent-muted text-accent rounded">
                                {fw}
                              </span>
                            ))}
                          </div>

                          <select
                            value={checklist[item.id]}
                            onChange={(e) => updateChecklistItem(item.id, e.target.value as ChecklistState[string])}
                            className="px-3 py-1 bg-background border border-border rounded text-foreground text-sm"
                          >
                            <option value="not-started">Not Started</option>
                            <option value="in-progress">In Progress</option>
                            <option value="completed">Completed</option>
                            <option value="not-applicable">Not Applicable</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Framework Coverage Summary */}
        <div className="mt-8 bg-accent-muted border border-border rounded-lg p-6">
          <h3 className="text-lg font-bold text-accent mb-4">Framework Coverage</h3>
          <div className="grid md:grid-cols-3 gap-4">
            {frameworks.map(framework => {
              const frameworkItems = checklistItems.filter(item => item.frameworks.includes(framework));
              const completed = frameworkItems.filter(item => checklist[item.id] === 'completed').length;
              const applicable = frameworkItems.filter(item => checklist[item.id] !== 'not-applicable').length;
              const percentage = applicable > 0 ? (completed / applicable) * 100 : 0;

              return (
                <div key={framework} className="bg-background rounded p-4">
                  <div className="font-bold text-foreground mb-2">{framework}</div>
                  <div className="text-2xl font-bold text-accent mb-1">{percentage.toFixed(0)}%</div>
                  <div className="text-xs text-foreground-tertiary">{completed} of {applicable} items</div>
                  <div className="mt-2 h-2 bg-surface rounded-full overflow-hidden">
                    <div className="h-full bg-accent transition-all" style={{ width: `${percentage}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Additional Resources */}
        <div className="mt-8 bg-surface rounded-lg border border-border p-6">
          <h3 className="text-lg font-bold text-foreground mb-3">Related Resources</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Link
              href="/frameworks"
              className="p-4 bg-background rounded border border-border-muted hover:border-border transition-colors"
            >
              <h4 className="font-bold text-foreground mb-1">Security Frameworks →</h4>
              <p className="text-sm text-foreground-tertiary">
                Detailed guidance on implementing security controls
              </p>
            </Link>
            <Link
              href="/templates"
              className="p-4 bg-background rounded border border-border-muted hover:border-border transition-colors"
            >
              <h4 className="font-bold text-foreground mb-1">Security Policy Templates →</h4>
              <p className="text-sm text-foreground-tertiary">
                Ready-to-use security policy documents
              </p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
